import { useState } from 'react';
import api from '../../services/api';
import config from '../../config';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  TableCellsIcon,
  SparklesIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';

const RssIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="6.18" cy="17.82" r="2.18"/>
    <path d="M4 4.44v2.83c7.03 0 12.73 5.7 12.73 12.73h2.83c0-8.59-6.97-15.56-15.56-15.56zm0 5.66v2.83c3.9 0 7.07 3.17 7.07 7.07h2.83c0-5.47-4.43-9.9-9.9-9.9z"/>
  </svg>
);

const AI_CREDITS_PER_SYNC = 10;

export default function RssFeedSource({ companyId, onSuccess, onBack }) {
  const [step, setStep] = useState(1);

  // Step 1: Feed URL
  const [url, setUrl] = useState('');
  const [feedInfo, setFeedInfo] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Step 2: Preview + optional AI
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [useAi, setUseAi] = useState(false);
  const [intentPrompt, setIntentPrompt] = useState('');
  const [aiPreview, setAiPreview] = useState(null);
  const [isRunningAi, setIsRunningAi] = useState(false);
  const [aiProgress, setAiProgress] = useState(null);
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleValidate = async () => {
    if (!url) {
      toast.error('Please enter an RSS feed URL');
      return;
    }
    setIsValidating(true);
    setFeedInfo(null);
    try {
      const res = await api.post('/rss-feed/validate', { url });
      if (res.data.valid) {
        setFeedInfo(res.data);
        toast.success(`Valid feed: "${res.data.title}" (${res.data.itemCount} items)`);
      } else {
        toast.error(res.data.error || 'Invalid RSS feed');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to validate feed');
    } finally {
      setIsValidating(false);
    }
  };

  const runSsePreview = async (intentPromptValue) => {
    let token = null;
    let selectedCompanyId = companyId;
    const authData = localStorage.getItem('neoshift-auth');
    if (authData) {
      try {
        const { state } = JSON.parse(authData);
        token = state?.accessToken;
        if (!selectedCompanyId) {
          selectedCompanyId = state?.selectedCompany?._id || state?.selectedCompany;
        }
      } catch (e) { /* ignore */ }
    }

    const body = { url, companyId: selectedCompanyId };
    if (intentPromptValue) body.intentPrompt = intentPromptValue;

    const response = await fetch(`${config.apiUrl}/rss-feed/preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 402) {
        throw new Error(`Insufficient credits. You need ${AI_CREDITS_PER_SYNC} credits for AI extraction.`);
      }
      throw new Error(errorData.error || 'Preview failed');
    }

    return response;
  };

  const parseSseStream = async (response, onProgress, onComplete) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (event.type === 'progress') {
            onProgress(event.data);
          } else if (event.type === 'complete') {
            onComplete(event.data);
          } else if (event.type === 'error') {
            throw new Error(event.data?.error || 'Preview failed');
          }
        } catch (e) {
          if (e.message && !e.message.includes('JSON')) throw e;
          console.error('Error parsing SSE:', e);
        }
      }
    }
  };

  const handlePreviewExtraction = async () => {
    if (!url) {
      toast.error('Please enter an RSS feed URL');
      return;
    }
    setIsPreviewing(true);
    setPreview(null);
    setPreviewProgress({ phase: 'starting', percentage: 0, message: 'Fetching RSS feed...' });

    try {
      const response = await runSsePreview(null);
      await parseSseStream(
        response,
        (data) => setPreviewProgress(data),
        (result) => {
          if (result.success) {
            setPreviewProgress({ phase: 'complete', percentage: 100, message: 'Preview ready!' });
            setPreview(result);
            setStep(2);
            if (!name) setName(result.feedTitle || 'RSS Feed Data');
          } else {
            setPreviewProgress(null);
            toast.error(result.error || 'Preview failed');
          }
        }
      );
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(error.message || 'Connection lost during preview. Please try again.');
      setPreviewProgress(null);
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleRunAiExtraction = async () => {
    if (!intentPrompt) {
      toast.error('Please describe what you want to extract');
      return;
    }
    setIsRunningAi(true);
    setAiPreview(null);
    setAiProgress({ phase: 'starting', percentage: 0, message: 'Starting AI extraction...' });

    try {
      const response = await runSsePreview(intentPrompt);
      await parseSseStream(
        response,
        (data) => setAiProgress(data),
        (result) => {
          if (result.success) {
            setAiProgress({ phase: 'complete', percentage: 100, message: 'AI extraction complete!' });
            setAiPreview(result);
          } else {
            setAiProgress(null);
            toast.error(result.error || 'AI extraction failed. Try simplifying your prompt.', { duration: 6000 });
          }
        }
      );
    } catch (error) {
      console.error('AI extraction error:', error);
      toast.error(error.message || 'AI extraction failed. Please try again.', { duration: 6000 });
      setAiProgress(null);
    } finally {
      setIsRunningAi(false);
    }
  };

  const handleDisableAi = () => {
    setUseAi(false);
    setAiPreview(null);
    setAiProgress(null);
    setIntentPrompt('');
  };

  const handleSave = async () => {
    if (!name) {
      toast.error('Please enter a name for this data source');
      return;
    }
    setIsSaving(true);

    const isAiMode = useAi && aiPreview;
    const activePreview = isAiMode ? aiPreview : preview;

    try {
      const res = await api.post('/rss-feed/create', {
        name,
        url,
        intentPrompt: isAiMode ? intentPrompt : null,
        aiEnriched: isAiMode,
        compiled: isAiMode ? activePreview.compiled : null,
        previewData: activePreview.previewData,
        feedTtl: activePreview.ttl || null,
        companyId,
      });

      if (res.data.success) {
        toast.success('RSS data source created!');
        onSuccess(res.data.dataSource);
      } else {
        toast.error(res.data.error || 'Failed to create data source');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create data source');
    } finally {
      setIsSaving(false);
    }
  };

  // Determine which data to display in the preview table
  const activeData = (useAi && aiPreview) ? aiPreview : preview;
  const displayRows = activeData?.previewData || [];
  const displayColumns = activeData?.columns || [];
  const filteredRows = (useAi && aiPreview)
    ? displayRows.filter(r => r.matches_user_intent)
    : displayRows;
  const visibleColumns = displayColumns.filter(c => c.name !== 'matches_user_intent');

  return (
    <div className="space-y-6">
      {/* Step indicators */}
      <div className="flex items-center gap-2 text-sm">
        {[
          { num: 1, label: 'Configure' },
          { num: 2, label: 'Preview & Create' },
        ].map(({ num, label }) => (
          <div key={num} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
              step >= num
                ? 'bg-primary-500 text-white'
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > num ? <CheckCircleIcon className="w-4 h-4" /> : num}
            </div>
            <span className={step >= num ? 'text-surface-900 dark:text-white' : 'text-surface-400'}>{label}</span>
            {num < 2 && <ChevronRightIcon className="w-4 h-4 text-surface-300" />}
          </div>
        ))}
      </div>

      {/* Step 1: Configure */}
      {step === 1 && (
        <div className="card p-6 space-y-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <RssIcon className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h3 className="font-semibold text-surface-900 dark:text-white">Configure RSS Feed</h3>
              <p className="text-sm text-surface-500 dark:text-surface-400">
                Enter a feed URL to preview its articles
              </p>
            </div>
          </div>

          {/* Feed URL */}
          <div>
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
              RSS Feed URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setFeedInfo(null); }}
                placeholder="https://techcrunch.com/feed/"
                className="input flex-1"
              />
              <button
                onClick={handleValidate}
                disabled={isValidating || !url}
                className="btn btn-secondary whitespace-nowrap"
              >
                {isValidating ? (
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                ) : (
                  'Validate'
                )}
              </button>
            </div>
            {feedInfo && (
              <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300 text-sm font-medium">
                  <CheckCircleIcon className="w-4 h-4" />
                  {feedInfo.title}
                </div>
                <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                  {feedInfo.itemCount} articles found
                  {feedInfo.ttl && ` · TTL: ${feedInfo.ttl} min`}
                </p>
              </div>
            )}
          </div>

          {/* Sample articles from validation */}
          {feedInfo?.sampleItems?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">Sample Articles</h4>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {feedInfo.sampleItems.map((item, i) => (
                  <div key={i} className="p-2 bg-surface-50 dark:bg-surface-800 rounded-lg text-sm">
                    <p className="font-medium text-surface-900 dark:text-white truncate">{item.title}</p>
                    <p className="text-xs text-surface-500 truncate">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview progress */}
          {isPreviewing && previewProgress && (
            <div className="space-y-3">
              <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2">
                <div
                  className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${previewProgress.percentage || 0}%` }}
                />
              </div>
              <div className="flex items-center gap-2 text-sm text-surface-600 dark:text-surface-400">
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                {previewProgress.message}
              </div>
            </div>
          )}

          <div className="flex justify-between items-center pt-2">
            <button onClick={onBack} className="btn btn-secondary">
              <ArrowLeftIcon className="w-4 h-4 mr-1" /> Back
            </button>
            <button
              onClick={handlePreviewExtraction}
              disabled={!feedInfo || isPreviewing}
              className="btn btn-primary"
            >
              {isPreviewing ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin mr-1" /> Fetching...
                </>
              ) : (
                <>
                  Preview Extraction <ChevronRightIcon className="w-4 h-4 ml-1" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Preview + optional AI + Create */}
      {step === 2 && preview && (
        <div className="space-y-6">
          {/* Data preview card */}
          <div className="card p-6 space-y-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <TableCellsIcon className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <h3 className="font-semibold text-surface-900 dark:text-white">
                  {preview.feedTitle || 'RSS Feed'}
                </h3>
                <p className="text-sm text-surface-500 dark:text-surface-400">
                  {preview.totalItems} articles available
                  {useAi && aiPreview && ` · ${filteredRows.length} matched your intent`}
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-surface-50 dark:bg-surface-800 rounded-lg text-center">
                <p className="text-2xl font-bold text-surface-900 dark:text-white">{preview.totalItems}</p>
                <p className="text-xs text-surface-500">Total Articles</p>
              </div>
              <div className="p-3 bg-surface-50 dark:bg-surface-800 rounded-lg text-center">
                <p className="text-2xl font-bold text-primary-600">{visibleColumns.length}</p>
                <p className="text-xs text-surface-500">Columns</p>
              </div>
              <div className="p-3 bg-surface-50 dark:bg-surface-800 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{filteredRows.length}</p>
                <p className="text-xs text-surface-500">Preview Rows</p>
              </div>
            </div>

            {/* Data table */}
            {filteredRows.length > 0 && (
              <div className="overflow-x-auto border border-surface-200 dark:border-surface-700 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-surface-800">
                    <tr>
                      {visibleColumns.map(col => (
                        <th key={col.name} className="px-3 py-2 text-left text-xs font-medium text-surface-500 uppercase whitespace-nowrap">
                          {col.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-surface-200 dark:divide-surface-700">
                    {filteredRows.map((row, i) => (
                      <tr key={i}>
                        {visibleColumns.map(col => (
                          <td key={col.name} className="px-3 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-[200px] truncate">
                            {row[col.name] != null ? String(row[col.name]) : '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* AI Enrichment card */}
          <div className="card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <SparklesIcon className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-surface-900 dark:text-white">AI Enrichment</h3>
                  <p className="text-sm text-surface-500 dark:text-surface-400">
                    Use AI to filter and extract specific data points from articles
                  </p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={useAi}
                  onChange={(e) => e.target.checked ? setUseAi(true) : handleDisableAi()}
                  className="sr-only peer"
                  disabled={isRunningAi}
                />
                <div className="w-11 h-6 bg-surface-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-surface-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-surface-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-surface-600 peer-checked:bg-purple-600"></div>
              </label>
            </div>

            {useAi && (
              <div className="space-y-4 pt-2">
                {/* Credit info */}
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start gap-2">
                  <CpuChipIcon className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-700 dark:text-amber-300">
                    <p className="font-medium">AI extraction costs {AI_CREDITS_PER_SYNC} credits per sync</p>
                    <p className="text-xs mt-0.5 text-amber-600 dark:text-amber-400">
                      Each scheduled or manual sync will use AI to process articles and consume credits.
                    </p>
                  </div>
                </div>

                {/* Intent prompt */}
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                    What do you want to extract?
                  </label>
                  <textarea
                    value={intentPrompt}
                    onChange={(e) => setIntentPrompt(e.target.value)}
                    placeholder="e.g. Find startup funding news and extract the company name, funding amount, funding round, and lead investor"
                    rows={3}
                    maxLength={2000}
                    className="input w-full"
                    disabled={isRunningAi}
                  />
                  <p className="text-xs text-surface-400 mt-1">
                    {intentPrompt.length}/2000 — Describe the articles you want AND the data points to extract.
                  </p>
                </div>

                {/* AI progress */}
                {isRunningAi && aiProgress && (
                  <div className="space-y-3">
                    <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${aiProgress.percentage || 0}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-surface-600 dark:text-surface-400">
                      <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      {aiProgress.message}
                    </div>
                  </div>
                )}

                {/* AI result summary */}
                {aiPreview && !isRunningAi && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <p className="text-sm text-green-700 dark:text-green-300">
                      AI extraction complete — {filteredRows.length} of {aiPreview.previewData?.length || 0} sample articles matched your intent with {visibleColumns.length} extracted columns.
                    </p>
                  </div>
                )}

                {aiPreview && filteredRows.length === 0 && !isRunningAi && (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      No sample articles matched your intent. The feed may still have matching articles later, or you can refine your prompt.
                    </p>
                  </div>
                )}

                <button
                  onClick={handleRunAiExtraction}
                  disabled={isRunningAi || !intentPrompt}
                  className="btn btn-secondary w-full"
                >
                  {isRunningAi ? (
                    <>
                      <ArrowPathIcon className="w-4 h-4 animate-spin mr-1" /> Running AI Extraction...
                    </>
                  ) : aiPreview ? (
                    <>
                      <SparklesIcon className="w-4 h-4 mr-1" /> Re-run AI Extraction
                    </>
                  ) : (
                    <>
                      <SparklesIcon className="w-4 h-4 mr-1" /> Run AI Extraction ({AI_CREDITS_PER_SYNC} credits)
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Name + Create card */}
          <div className="card p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-1">
                Data Source Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. TechCrunch Funding News"
                maxLength={100}
                className="input w-full"
              />
            </div>

            <p className="text-xs text-surface-500 dark:text-surface-400">
              After creating, you can configure an automatic sync schedule on the Data Source details page.
            </p>

            <div className="flex justify-between items-center pt-2">
              <button onClick={() => { setStep(1); setPreview(null); setAiPreview(null); setUseAi(false); }} className="btn btn-secondary">
                <ArrowLeftIcon className="w-4 h-4 mr-1" /> Reconfigure
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !name || (useAi && !aiPreview)}
                className="btn btn-primary"
              >
                {isSaving ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin mr-1" /> Creating...
                  </>
                ) : (
                  <>
                    Create Data Source <CheckCircleIcon className="w-4 h-4 ml-1" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
